import { Paper, Title, Text, Stack, Group, Box, Button } from '@mantine/core';
import { IconMapPin, IconPhone, IconNavigation, IconExternalLink } from '@tabler/icons-react';

interface ClinicMapProps {
  address?: string;
}

export function ClinicMap({ address = "Stampemøllevej 21, 8700 Horsens, Denmark" }: ClinicMapProps) {
  const handleDirectionsClick = () => {
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(directionsUrl, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = 'tel:+4560803735';
  };

  return (
    <Paper 
      withBorder 
      shadow="sm" 
      p="lg" 
      radius="md"
      className="clinic-map-container"
    >
      <Stack gap="md">
        <Group gap="xs">
          <IconMapPin size={24} />
          <Title order={3} className="clinic-map-title">
            Find Klinikken
          </Title>
        </Group>

        {/* Google Maps Embed */}
        <Box 
          style={{ 
            position: 'relative', 
            width: '100%', 
            height: '300px', 
            borderRadius: '8px', 
            overflow: 'hidden' 
          }}
          className="clinic-map-iframe-container"
        >
          <iframe
            src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Lykke Terapien Location - Google Maps"
            className="clinic-map-iframe"
          />
        </Box>

        {/* Contact Information */}
        <Stack gap="sm" className="clinic-contact-info">
          <Group gap="xs" wrap="nowrap">
            <IconMapPin size={16} style={{ flexShrink: 0 }} />
            <Text size="sm" className="clinic-address">
              {address}
            </Text>
          </Group>
          
          <Group gap="xs" wrap="nowrap">
            <IconPhone size={16} style={{ flexShrink: 0 }} />
            <Text size="sm">
              <a 
                href="tel:+4560803735" 
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'none' 
                }}
                className="clinic-phone-link"
              >
                +45 60 80 37 35
              </a>
            </Text>
          </Group>
        </Stack>

        {/* Action Buttons */}
        <Group gap="sm" className="clinic-action-buttons">
          <Button
            leftSection={<IconNavigation size={16} />}
            onClick={handleDirectionsClick}
            variant="filled"
            size="sm"
            className="directions-button"
          >
            Få vejvisning
          </Button>
          
          <Button
            leftSection={<IconPhone size={16} />}
            onClick={handleCallClick}
            variant="outline"
            size="sm"
            className="call-button"
          >
            Ring nu
          </Button>
        </Group>

        {/* Additional Info */}
        <Text size="xs" c="dimmed" className="clinic-additional-info">
          Klik på kortet for at åbne i Google Maps app eller browser
        </Text>
      </Stack>

      <style>{`
        /* Mobile-specific improvements */
        @media (max-width: 768px) {
          .clinic-map-container {
            padding: 1rem !important;
          }
          
          .clinic-map-title {
            font-size: 1.25rem !important;
          }
          
          .clinic-map-iframe-container {
            height: 250px !important;
            border-radius: 6px !important;
          }
          
          .clinic-contact-info {
            gap: 0.75rem !important;
          }
          
          .clinic-address {
            font-size: 0.875rem !important;
            line-height: 1.4 !important;
          }
          
          .clinic-phone-link:hover {
            text-decoration: underline !important;
          }
          
          .clinic-action-buttons {
            flex-direction: column !important;
            gap: 0.5rem !important;
          }
          
          .directions-button,
          .call-button {
            width: 100% !important;
            font-size: 0.875rem !important;
            min-height: 44px !important;
          }
          
          .clinic-additional-info {
            text-align: center !important;
            font-size: 0.75rem !important;
          }
          
          /* Better touch interaction for iframe */
          .clinic-map-iframe {
            pointer-events: auto !important;
          }
        }
        
        /* Tablet improvements */
        @media (min-width: 769px) and (max-width: 1024px) {
          .clinic-map-container {
            padding: 1.5rem !important;
          }
          
          .clinic-map-iframe-container {
            height: 280px !important;
          }
          
          .clinic-action-buttons {
            justify-content: center !important;
          }
        }
        
        /* Enhanced interaction states */
        .directions-button:hover {
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
          transition: all 0.2s ease !important;
        }
        
        .call-button:hover {
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
          transition: all 0.2s ease !important;
        }
        
        /* Accessibility improvements */
        .clinic-map-iframe:focus {
          outline: 2px solid var(--mantine-color-blue-6) !important;
          outline-offset: 2px !important;
        }
        
        .clinic-phone-link:focus {
          outline: 2px solid var(--mantine-color-blue-6) !important;
          outline-offset: 1px !important;
          border-radius: 2px !important;
        }
      `}</style>
    </Paper>
  );
} 